import { FC, useState } from "react"
import { Link } from "react-router-dom"
import { SupportIssueModal } from "~/shared-components"
import { LoginModalEcomm } from "~/views/auth/login"
import { useQueryClient } from "react-query"
import { useShopContext } from "~/contexts"
import { APPLICATION, useAuth, useIsUser, UserId, useUserCanUse } from "@ncs/ncs-api"
import {
	AnimatedEntrance,
	Box,
	Button,
	Paragraph,
	useScreenSizeMatch,
	TrackingEvent,
	Icon,
	ButtonEcomm,
} from "@ncs/web-legos"
import { MobileAccountMenuModal } from "../MobileAccountMenuModal"
import { useAnalytics } from "~/hooks/useAnalytics"
import {
	accountMenuStyle,
	firstnameStyle,
	separatorStyle,
	submenuContainerStyle,
	userIconStyle,
	usernameStyle,
	userWelcomeStyle,
	buttonStyle,
} from "./styles"

export const ShopBarUserWelcome: FC = () => {
	const { user, logout } = useAuth()
	const [showTicketModal, setShowTicketModal] = useState(false)
	const screenIsMediumUp = useScreenSizeMatch("md", "up")
	const canUseConnectivity = useUserCanUse(APPLICATION.Connectivity)
	const canUseEquipment = useUserCanUse(APPLICATION.Equipment)
	const canUseDispatch = useUserCanUse([APPLICATION.CreateDispatch, APPLICATION.SearchDispatch])
	const isDb = useIsUser(UserId.DrivenBrands)
	const [isLoginOpen, setIsLoginOpen] = useState(false)
	const [showAcountSubmenu, setShowAcountSubmenu] = useState(false)
	const [showModalAcountSubmenu, setShowModalAcountSubmenu] = useState(false)
	const queryClient = useQueryClient()
	const [, shopDispatch] = useShopContext()
	const { resetAnalytics, trackEvent } = useAnalytics()

	const handleMouseEnter = () => {
		if (screenIsMediumUp) {
			setShowAcountSubmenu(true)
		}
	}

	const accountMenuHandler = () => {
		if (!screenIsMediumUp) {
			setShowModalAcountSubmenu(true)
		}
	}

	const closeAccountModalHandler = () => {
		if (!screenIsMediumUp) {
			setShowModalAcountSubmenu(false)
		}
	}

	const loginHandler = () => {
		setIsLoginOpen(true)
		trackEvent(TrackingEvent.LOGIN_VIEW)
	}

	const onLoginClose = () => {
		setIsLoginOpen(false)
	}

	const logoutHandler = () => {
		shopDispatch({
			type: "reset checkout state",
		})
		logout()
		queryClient.removeQueries()
		resetAnalytics()
		trackEvent(TrackingEvent.LOG_OUT)
	}

	const contactUsHandler = () => {
		setShowTicketModal(true)
		trackEvent(TrackingEvent.CONTACT_US)
	}

	return (
		<>
			<LoginModalEcomm isOpen={isLoginOpen} onClose={onLoginClose} />
			<SupportIssueModal
				isOpen={showTicketModal}
				onClose={() => setShowTicketModal(false)}
			/>
			{user ?
				<Box display="flex" alignItems="center">
					<div onMouseEnter={handleMouseEnter}>
						<span css={userWelcomeStyle}>
							<Box display="flex" alignItems="center" columnGap={0.75}>
								<ButtonEcomm
									icon="circle-user"
									onClick={accountMenuHandler}
									css={userIconStyle}
									aria-label="User account menu handler"
								/>
								{screenIsMediumUp && (
									<Box display="flex" flexDirection="column" flexShrink={0}>
										<Paragraph bold small color="white" css={firstnameStyle}>
											{user.name.split(" ")[0]}
										</Paragraph>
									</Box>
								)}
							</Box>
						</span>
					</div>
					<div
						onMouseLeave={() => {
							setShowAcountSubmenu(false)
						}}
					>
						<AnimatedEntrance
							show={showAcountSubmenu}
							css={submenuContainerStyle}
							duration={200}
							delay={200}
						>
							{user && <div css={usernameStyle}>{user.name}</div>}
							<ul css={accountMenuStyle}>
								{!isDb && (
									<li css={separatorStyle}>
										<Link
											onClick={() => trackEvent(TrackingEvent.MY_ACCOUNT)}
											to={"/account"}
										>
											My Account
										</Link>
									</li>
								)}
								{!isDb && canUseDispatch && (
									<li>
										<Link
											onClick={() =>
												trackEvent(TrackingEvent.MY_SERVICE_REQUESTS)
											}
											to={"/service-requests"}
										>
											My Service Requests
										</Link>
									</li>
								)}
								{!isDb && canUseEquipment && (
									<li>
										<Link
											onClick={() => trackEvent(TrackingEvent.MY_EQUIPMENT)}
											to={"/my-equipment"}
										>
											My Equipment
										</Link>
									</li>
								)}
								{canUseConnectivity && (
									<li>
										<Link
											onClick={() => trackEvent(TrackingEvent.CONNECTIVITY)}
											to={"/connectivity"}
										>
											Connectivity
										</Link>
									</li>
								)}
								<li css={separatorStyle}>
									<button css={buttonStyle} onClick={contactUsHandler}>
										Contact Us
									</button>
								</li>
								<li css={separatorStyle}>
									<Link onClick={logoutHandler} to={""}>
										<Icon icon="arrow-right-from-bracket" />
										Logout
									</Link>
								</li>
							</ul>
						</AnimatedEntrance>
					</div>
				</Box>
			:	<Box display="flex" alignItems="center" onMouseEnter={handleMouseEnter}>
					<Button onClick={loginHandler}>
						<Paragraph bold small color="white" css={firstnameStyle}>
							Login / Signup
						</Paragraph>
					</Button>
				</Box>
			}
			<MobileAccountMenuModal
				isOpen={showModalAcountSubmenu}
				onClose={closeAccountModalHandler}
			/>
		</>
	)
}
